/**
 * VITAL — Interactions & Animation System
 * Scroll reveal · Counter animation · Smooth scroll · Skeleton reveal
 * Respeta prefers-reduced-motion en todos los efectos
 */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ═══════════════════════════════════════════
     1. SCROLL REVEAL
     Usa: data-reveal en el elemento raíz
          data-reveal-delay="100" para stagger (ms)
          data-reveal-from="bottom|left|right|fade"
  ════════════════════════════════════════════ */
  function initScrollReveal() {
    var els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;

    if (prefersReduced) {
      els.forEach(function (el) { el.classList.add('v-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
        setTimeout(function () {
          el.classList.add('v-revealed');
        }, delay);
        io.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    els.forEach(function (el) {
      el.classList.add('v-reveal-init');
      io.observe(el);
    });

    /* Red de seguridad: si el observer no dispara (fallo de compositing,
       navegador raro), NADA puede quedar invisible. A los 3s se revela todo. */
    setTimeout(function () {
      els.forEach(function (el) { el.classList.add('v-revealed'); });
    }, 3000);
  }

  /* ═══════════════════════════════════════════
     2. STAGGER CHILDREN
     Usa: data-stagger en el contenedor padre
          Los hijos directos reciben delay automático
  ════════════════════════════════════════════ */
  function initStagger() {
    var parents = document.querySelectorAll('[data-stagger]');
    if (!parents.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var parent = entry.target;
        var children = parent.children;
        var baseDelay = parseInt(parent.getAttribute('data-stagger-delay') || '80', 10);

        Array.prototype.forEach.call(children, function (child, i) {
          if (prefersReduced) {
            child.classList.add('v-revealed');
          } else {
            child.classList.add('v-reveal-init');
            setTimeout(function () {
              child.classList.add('v-revealed');
            }, i * baseDelay);
          }
        });
        io.unobserve(parent);
      });
    }, { threshold: 0.10, rootMargin: '0px 0px -30px 0px' });

    parents.forEach(function (el) { io.observe(el); });
  }

  /* ═══════════════════════════════════════════
     3. COUNTER ANIMATION
     Usa: data-counter="5000" (valor final)
          data-counter-suffix="+" o "%"
          data-counter-prefix="$"
          data-counter-duration="1600" (ms)
  ════════════════════════════════════════════ */
  function initCounters() {
    var els = document.querySelectorAll('[data-counter]');
    if (!els.length) return;

    function animateCounter(el) {
      var target = parseFloat(el.getAttribute('data-counter') || '0');
      var suffix = el.getAttribute('data-counter-suffix') || '';
      var prefix = el.getAttribute('data-counter-prefix') || '';
      var duration = parseInt(el.getAttribute('data-counter-duration') || '1400', 10);
      var decimals = (target % 1 !== 0) ? 1 : 0;
      var start = null;

      if (prefersReduced) {
        el.textContent = prefix + target.toFixed(decimals) + suffix;
        return;
      }

      function step(ts) {
        if (!start) start = ts;
        var progress = Math.min((ts - start) / duration, 1);
        /* ease-out quart */
        var eased = 1 - Math.pow(1 - progress, 4);
        var current = (target * eased);
        el.textContent = prefix + current.toFixed(decimals) + suffix;
        if (progress < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        animateCounter(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.5 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ═══════════════════════════════════════════
     4. SMOOTH SCROLL (anchor links)
     Respeta el header fijo
  ════════════════════════════════════════════ */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      var hash = anchor.getAttribute('href');
      if (hash === '#') return;
      var target = document.querySelector(hash);
      if (!target) return;
      e.preventDefault();
      var headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0', 10);
      var top = target.getBoundingClientRect().top + window.scrollY - headerH - 16;
      if (prefersReduced) {
        window.scrollTo(0, top);
      } else {
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
      /* Update URL */
      history.pushState(null, null, hash);
    });
  }

  /* ═══════════════════════════════════════════
     5. SKELETON → CONTENT
     Usa: data-skeleton en el wrapper
     Quita clase v-skeleton-active cuando carga
  ════════════════════════════════════════════ */
  function initSkeletons() {
    var skeletons = document.querySelectorAll('[data-skeleton]');
    skeletons.forEach(function (el) {
      el.classList.add('v-skeleton-active');
      /* Si tiene imágenes, espera a que carguen */
      var imgs = el.querySelectorAll('img');
      if (imgs.length === 0) {
        el.classList.remove('v-skeleton-active');
        return;
      }
      var loaded = 0;
      function onLoad() {
        loaded++;
        if (loaded >= imgs.length) el.classList.remove('v-skeleton-active');
      }
      imgs.forEach(function (img) {
        if (img.complete) { onLoad(); }
        else { img.addEventListener('load', onLoad); img.addEventListener('error', onLoad); }
      });
    });
  }

  /* ═══════════════════════════════════════════
     6. HOVER PARALLAX (product cards, desktop only)
     Usa: data-tilt en la tarjeta
  ════════════════════════════════════════════ */
  function initTilt() {
    if (prefersReduced || window.innerWidth < 1024) return;
    var cards = document.querySelectorAll('[data-tilt]');
    cards.forEach(function (card) {
      card.addEventListener('mousemove', function (e) {
        var rect = card.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dx = (e.clientX - cx) / (rect.width / 2);
        var dy = (e.clientY - cy) / (rect.height / 2);
        var rotX = (-dy * 4).toFixed(2);
        var rotY = (dx * 4).toFixed(2);
        card.style.transform = 'perspective(800px) rotateX(' + rotX + 'deg) rotateY(' + rotY + 'deg) translateZ(4px)';
      });
      card.addEventListener('mouseleave', function () {
        card.style.transform = '';
      });
    });
  }

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function boot() {
    initScrollReveal();
    initStagger();
    initCounters();
    initSmoothScroll();
    initSkeletons();
    initTilt();
  }

})();
